import { logger } from '@/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioControls } from './useAudioControls';

/**
 * All audio playback business logic lives here.
 *
 * Owns:
 *  - the audioRef (attached to the <audio> DOM node inside AudioPlayer)
 *  - all playback state: isPlaying, progress, currentTime
 *  - all imperative controls: play, pause, seek
 *  - native event listener registration/cleanup
 *
 * Nothing audio-related should exist outside this hook.
 */
export const useAudioPlayerViewModel = () => {
   const [isPlaying, setIsPlaying] = useState(false);
   const [progress, setProgress] = useState(0);
   const [currentTime, setCurrentTime] = useState(0);

   const audioRef = useRef(null);
   const wasPlayingRef = useRef(false);

   // audioRef is a stable object — these helpers never need to be recreated
   const executePlay = useCallback(() => {
      audioRef.current?.play()?.catch(err => {
         if (logger?.warn) {
            logger.warn('Play interrupted:', err);
         } else {
            console.error(err);
         }
      });
   }, []);

   const executePause = useCallback(() => {
      audioRef.current?.pause();
   }, []);

   const { togglePlayback, handleKeyDown } = useAudioControls(isPlaying, executePlay, executePause);

   // Register native listeners once on mount.
   // audioSrc changes update the <audio src> attribute via React reconciliation —
   // no need to re-register listeners when the source changes.
   useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const updateProgress = () => {
         const t = audio.currentTime;
         setCurrentTime(t);
         setProgress((t / (audio.duration || 1)) * 100);
      };

      const handlePlay = () => setIsPlaying(true);
      const handlePause = () => setIsPlaying(false);
      const handleEnded = () => {
         setIsPlaying(false);
         setProgress(0);
         setCurrentTime(0);
      };

      const handleInterrupt = () => audio.pause();

      audio.addEventListener('timeupdate', updateProgress);
      audio.addEventListener('play', handlePlay);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('ended', handleEnded);
      window.addEventListener('AUDIO_INTERRUPT_EVENT', handleInterrupt);

      return () => {
         audio.removeEventListener('timeupdate', updateProgress);
         audio.removeEventListener('play', handlePlay);
         audio.removeEventListener('pause', handlePause);
         audio.removeEventListener('ended', handleEnded);
         window.removeEventListener('AUDIO_INTERRUPT_EVENT', handleInterrupt);
      };
   }, []);

   const handleSeekStart = useCallback(
      e => {
         e?.stopPropagation();
         if (!audioRef.current) return;
         wasPlayingRef.current = !audioRef.current.paused;
         if (wasPlayingRef.current) executePause();
      },
      [executePause],
   );

   const handleSeek = useCallback(e => {
      e?.stopPropagation();
      if (!audioRef.current) return;
      const val = +e.target.value;
      audioRef.current.currentTime = (val / 100) * (audioRef.current.duration || 0);
      setProgress(val);
   }, []);

   const handleSeekEnd = useCallback(
      e => {
         e?.stopPropagation();
         if (wasPlayingRef.current) executePlay();
      },
      [executePlay],
   );

   return {
      audioRef, // returned so AudioPlayer can attach it to the <audio> DOM node
      state: { isPlaying, progress, currentTime },
      handlers: { togglePlayback, handleKeyDown, handleSeekStart, handleSeek, handleSeekEnd },
   };
};
